import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft, CalendarClock, CheckCircle2, ClipboardList, FileBarChart2, Flag, Target, UserRound } from 'lucide-react';

import api from '../api';
import GoalTasksList from './GoalTasksList';
import ProgressUpdateForm from './ProgressUpdateForm';
import { formatDate, formatModuleFieldValue, formatNumber, getModuleFieldConfigs, getModuleMeta, GOALS_TEXT_SCOPE_STYLES, isEmptyObject, normalizeEvidencePairs, prettyLabel, statusStyle } from './shared';

const Wrap = styled.div`
  display: grid;
  gap: 20px;
  max-width: 1480px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #dbe5f1;
  border-radius: 24px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  padding: 22px;
`;

const Hero = styled(Card)`
  background:
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.18), transparent 25%),
    linear-gradient(135deg, #f8fdff 0%, #f8fbff 42%, #ffffff 100%);
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  border: 1px solid #dbe5f1;
  border-radius: 18px;
  padding: 16px;
  background: ${(props) => props.$bg || '#ffffff'};
`;

const TabBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  border: 1px solid ${(props) => (props.$active ? '#2563eb' : '#c9d8ea')};
  background: ${(props) => (props.$active ? '#2563eb' : '#ffffff')};
  color: ${(props) => (props.$active ? '#ffffff' : '#0f172a')};
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 800;
  cursor: pointer;
`;

const EmptyState = ({ title, description }) => (
  <div style={{ border: '1px dashed #c7d6e6', borderRadius: 18, background: '#f8fbff', padding: 18 }}>
    <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
    <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>{description}</div>
  </div>
);

function MetadataSummary({ metadata }) {
  if (isEmptyObject(metadata)) {
    return <EmptyState title="No extra goal details yet" description="Admin has not added any additional goal metadata for this record." />;
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {Object.entries(metadata).map(([key, value]) => (
        <div key={key} style={{ border: '1px solid #dbe5f1', borderRadius: 16, background: '#ffffff', padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800 }}>{prettyLabel(key)}</div>
          <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</div>
        </div>
      ))}
    </div>
  );
}

function ModuleFieldsSummary({ moduleName, values }) {
  const configs = getModuleFieldConfigs(moduleName);
  const meta = getModuleMeta(moduleName);
  const visibleFields = configs.filter((field) => {
    const value = values?.[field.name];
    if (field.type === 'boolean') return typeof value === 'boolean';
    if (Array.isArray(value)) return value.length > 0;
    return value != null && value !== '';
  });

  if (!visibleFields.length) {
    return <EmptyState title="No module-specific details yet" description="This goal does not have any saved module metrics yet." />;
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {visibleFields.map((field) => (
        <div key={field.name} style={{ border: `1px solid ${meta.border}`, borderRadius: 16, background: '#ffffff', padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>{field.label}</div>
          <div style={{ marginTop: 6, fontSize: 15, lineHeight: 1.6, fontWeight: 800 }}>{formatModuleFieldValue(field, values[field.name])}</div>
        </div>
      ))}
    </div>
  );
}

export default function GoalDetailView({ goalId, canManage = false, onClose, onRefresh, adminView = false, initialTab = 'overview' }) {
  const [goal, setGoal] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [reviewComments, setReviewComments] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [userMap, setUserMap] = useState({});

  const loadGoal = async () => {
    try {
      const response = await api.get(`/goals/${goalId}`);
      setGoal(response.data?.goal || null);
    } catch {
      setGoal(null);
    }
  };

  useEffect(() => {
    loadGoal();
  }, [goalId]);

  useEffect(() => {
    setTab(initialTab || 'overview');
  }, [initialTab, goalId]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await api.get('/goals/meta/users');
        const nextMap = {};
        (response.data?.data || []).forEach((user) => {
          nextMap[user.id] = user.username || user.email || `User ${user.id}`;
        });
        setUserMap(nextMap);
      } catch {
        setUserMap({});
      }
    };
    loadUsers();
  }, []);

  const latestProgress = (goal?.progress_updates || [])[0] || null;
  const latestEvidence = useMemo(() => normalizeEvidencePairs(latestProgress?.supporting_data), [latestProgress]);

  const submitReview = async () => {
    if (!reviewComments.trim()) return;
    setReviewSaving(true);
    try {
      await api.post(`/goals/${goal.id}/review`, { review_comments: reviewComments });
      setReviewComments('');
      await loadGoal();
      onRefresh?.();
    } finally {
      setReviewSaving(false);
    }
  };

  if (!goal) {
    return <Card className="goals-text-scope"><style>{GOALS_TEXT_SCOPE_STYLES}</style>Loading goal details...</Card>;
  }

  const badge = statusStyle(goal.status);
  const daysRemaining = goal.days_remaining ?? 0;
  const userName = (userId) => (!userId ? 'Not available' : userMap[userId] || `User ${userId}`);
  const moduleMeta = getModuleMeta(goal.assigned_module);
  const canSubmitProgress = canManage && !adminView;
  const canCreateTasks = canManage;

  return (
    <Wrap className="goals-text-scope">
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>

      <Hero>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 860 }}>
            <button onClick={onClose} style={{ border: '1px solid #c9d8ea', borderRadius: 999, background: '#ffffff', color: '#0f172a', padding: '10px 16px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <ArrowLeft size={16} />
              Back To Goals
            </button>
            <div style={{ marginTop: 18, fontSize: 12, fontWeight: 900, letterSpacing: '0.12em' }}>{adminView ? 'ADMIN GOAL REVIEW' : 'GOAL EXECUTION PAGE'}</div>
            <div style={{ marginTop: 10, fontSize: 34, fontWeight: 900, lineHeight: 1.1 }}>{goal.title}</div>
            <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.7 }}>{goal.description || 'No description provided yet.'}</div>
          </div>

          <div style={{ display: 'grid', gap: 12, minWidth: 240 }}>
            <span style={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', padding: '9px 14px', borderRadius: 999, background: badge.bg, color: badge.text, border: `1px solid ${badge.border}`, fontWeight: 800 }}>
              {prettyLabel(goal.status)}
            </span>
            <div style={{ border: '1px solid #dbe5f1', borderRadius: 18, padding: 16, background: '#ffffff' }}>
              <div style={{ fontSize: 12, fontWeight: 800 }}>Completion Progress</div>
              <div style={{ marginTop: 8, fontSize: 28, fontWeight: 900 }}>{formatNumber(goal.completion_percentage)}%</div>
              <div style={{ marginTop: 10, height: 10, borderRadius: 999, background: '#e8eef6', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, Math.max(0, Number(goal.completion_percentage || 0)))}%`, height: '100%', background: 'linear-gradient(90deg,#38bdf8 0%,#2563eb 100%)' }} />
              </div>
            </div>
          </div>
        </div>

        <StatGrid style={{ marginTop: 22 }}>
          <StatCard><div style={{ fontSize: 12, fontWeight: 800 }}>Current Value</div><div style={{ marginTop: 8, fontSize: 24, fontWeight: 900 }}>{formatNumber(goal.current_value)} {goal.target_unit}</div></StatCard>
          <StatCard><div style={{ fontSize: 12, fontWeight: 800 }}>Target Value</div><div style={{ marginTop: 8, fontSize: 24, fontWeight: 900 }}>{formatNumber(goal.target_value)} {goal.target_unit}</div></StatCard>
          <StatCard><div style={{ fontSize: 12, fontWeight: 800 }}>Deadline</div><div style={{ marginTop: 8, fontSize: 24, fontWeight: 900 }}>{formatDate(goal.end_date)}</div></StatCard>
          <StatCard><div style={{ fontSize: 12, fontWeight: 800 }}>Days Remaining</div><div style={{ marginTop: 8, fontSize: 24, fontWeight: 900 }}>{daysRemaining}</div></StatCard>
        </StatGrid>
      </Hero>

      <TabBar>
        {['overview', 'progress', 'tasks', 'history'].map((item) => (
          <TabButton key={item} $active={tab === item} onClick={() => setTab(item)}>
            {prettyLabel(item)}
          </TabButton>
        ))}
      </TabBar>

      {tab === 'overview' ? (
        <div style={{ display: 'grid', gridTemplateColumns: adminView ? 'minmax(0, 1.25fr) minmax(320px, 0.75fr)' : '1fr', gap: 20 }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Target size={18} color="#2563eb" />
              <div style={{ fontWeight: 900, fontSize: 20 }}>Goal Summary</div>
            </div>

            <StatGrid style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Module</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{prettyLabel(goal.assigned_module)}</div></StatCard>
              <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Review Frequency</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{prettyLabel(goal.review_frequency)}</div></StatCard>
              <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Start Date</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{formatDate(goal.start_date)}</div></StatCard>
              <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Last Reviewed</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{formatDate(goal.last_reviewed_at)}</div></StatCard>
            </StatGrid>

            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <FileBarChart2 size={18} color="#2563eb" />
                <div style={{ fontWeight: 900, fontSize: 18 }}>Latest Evidence Snapshot</div>
              </div>
              {latestEvidence.length ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {latestEvidence.map((entry) => (
                    <div key={entry.key} style={{ border: '1px solid #dbe5f1', borderRadius: 16, background: '#f8fbff', padding: 14, display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12 }}>
                      <div style={{ fontWeight: 800 }}>{prettyLabel(entry.key)}</div>
                      <div style={{ lineHeight: 1.6 }}>{entry.value}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No evidence added yet" description="Once progress is updated, the latest supporting evidence will appear here in a simpler, readable layout." />
              )}
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <CheckCircle2 size={18} color={moduleMeta.color} />
                <div style={{ fontWeight: 900, fontSize: 18 }}>{prettyLabel(goal.assigned_module)} Metrics</div>
              </div>
              <ModuleFieldsSummary moduleName={goal.assigned_module} values={goal.module_fields_config || {}} />
            </div>

            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Additional Goal Information</div>
              <MetadataSummary metadata={goal.metadata} />
            </div>
          </Card>

          {adminView ? (
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <UserRound size={18} color="#2563eb" />
                <div style={{ fontWeight: 900, fontSize: 20 }}>Admin Control Center</div>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Assigned User</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{goal.assigned_to_user_id ? userName(goal.assigned_to_user_id) : 'Unassigned'}</div></StatCard>
                <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Priority</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{goal.priority || 3} / 5</div></StatCard>
                <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Created By</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{goal.created_by ? userName(goal.created_by) : '-'}</div></StatCard>
                <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Last Reviewed By</div><div style={{ marginTop: 8, fontSize: 18, fontWeight: 900 }}>{goal.last_reviewed_by ? userName(goal.last_reviewed_by) : 'Not reviewed yet'}</div></StatCard>
              </div>

              <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 18 }}>
                <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Add Review Comment</div>
                <textarea
                  rows="5"
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder="Add a review note for managers and stakeholders"
                  style={{ width: '100%', padding: 12, borderRadius: 14, border: '1px solid #cbd5e1', background: '#fff', resize: 'vertical' }}
                />
                <button
                  type="button"
                  onClick={submitReview}
                  disabled={reviewSaving}
                  style={{ marginTop: 12, border: 0, borderRadius: 12, background: '#0f766e', color: '#fff', padding: '12px 14px', fontWeight: 800, cursor: 'pointer', width: '100%' }}
                >
                  {reviewSaving ? 'Saving Review...' : 'Save Admin Review'}
                </button>
              </div>
            </Card>
          ) : null}
        </div>
      ) : null}

      {tab === 'progress' ? (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <Flag size={18} color="#2563eb" />
            <div style={{ fontWeight: 900, fontSize: 20 }}>{adminView ? 'Progress Oversight' : 'Progress Updates'}</div>
          </div>

          <div style={{ display: 'grid', gap: 18 }}>
            {adminView ? (
              <div style={{ border: '1px solid #dbe5f1', borderRadius: 18, background: '#f8fbff', padding: 16 }}>
                <div style={{ fontWeight: 900, fontSize: 16 }}>Admin Oversight Note</div>
                <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.7 }}>
                  Admin can review submitted progress, evidence, and trends here, but day-to-day execution updates belong to the assigned module teams. This keeps admin in a supervisory role instead of acting like an operator.
                </div>
              </div>
            ) : null}
            {canSubmitProgress ? <ProgressUpdateForm goal={goal} onSaved={async () => { await loadGoal(); onRefresh?.(); }} /> : null}

            {(goal.progress_updates || []).length ? (
              <div style={{ display: 'grid', gap: 14 }}>
                {goal.progress_updates.map((item) => {
                  const evidence = normalizeEvidencePairs(item.supporting_data);
                  return (
                    <div key={item.id} style={{ border: '1px solid #dbe5f1', borderRadius: 18, background: '#f8fbff', padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 900, fontSize: 16 }}>{formatDate(item.update_date)}</div>
                          <div style={{ marginTop: 4, fontSize: 14 }}>{item.milestone_reached || 'Progress update'}</div>
                        </div>
                        <div style={{ padding: '6px 10px', borderRadius: 999, background: '#dbeafe', color: '#1d4ed8', fontWeight: 800 }}>
                          {formatNumber(item.progress_percentage)}%
                        </div>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>{item.update_notes || 'No notes added.'}</div>
                      {evidence.length ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                          {evidence.map((entry) => (
                            <span key={`${item.id}-${entry.key}`} style={{ padding: '7px 10px', borderRadius: 999, background: '#ffffff', border: '1px solid #bfdbfe', fontSize: 12, fontWeight: 700 }}>
                              {prettyLabel(entry.key)}: {entry.value}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No progress updates yet" description={adminView ? 'Module teams have not submitted any progress yet.' : 'Managers can start tracking movement here once work begins.'} />
            )}
          </div>
        </Card>
      ) : null}

      {tab === 'tasks' ? (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <ClipboardList size={18} color="#2563eb" />
            <div style={{ fontWeight: 900, fontSize: 20 }}>{adminView ? 'Task Governance, Proof Review & Scoring' : 'Task Wise Work, Proof & Completion'}</div>
          </div>
          <div style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.7 }}>
            {adminView
              ? 'Admin can create tasks, review task progress, inspect proof submitted by teams, and score completion quality without using the same execution controls as module users.'
              : 'Each task has its own action area. Teams upload proof on the task itself, and Admin can review and score from the same card.'}
          </div>
          <GoalTasksList goal={goal} canCreate={canCreateTasks} adminView={adminView} />
        </Card>
      ) : null}

      {tab === 'history' ? (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <CalendarClock size={18} color="#2563eb" />
            <div style={{ fontWeight: 900, fontSize: 20 }}>{adminView ? 'Audit & Review History' : 'Goal History'}</div>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Created</div><div style={{ marginTop: 8, fontSize: 16, fontWeight: 900 }}>{formatDate(goal.created_at)}</div></StatCard>
            <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Updated</div><div style={{ marginTop: 8, fontSize: 16, fontWeight: 900 }}>{formatDate(goal.updated_at)}</div></StatCard>
            <StatCard $bg="#f8fbff"><div style={{ fontSize: 12, fontWeight: 800 }}>Last Reviewed</div><div style={{ marginTop: 8, fontSize: 16, fontWeight: 900 }}>{formatDate(goal.last_reviewed_at)}</div></StatCard>
          </div>
        </Card>
      ) : null}
    </Wrap>
  );
}
